import {type TypedUseSelectorHook, useSelector as rawUseSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import {type RootState} from '../app/store';
import {type Organization, type Project, type User} from '../types';

const useSelector: TypedUseSelectorHook<RootState> = rawUseSelector;

export const useSelectedOrganizationId = () => {
  const params = useParams();
  return params.orgId;
};

export const useSelectedIndexName = () => {
  const params = useParams();
  return params.indexName;
};

export const useIndex = (projectId: string, indexName: string) => {
  return useSelector((state) =>
    state.projects.indexes[projectId]?.find((index) => index.index_name === indexName),
  );
};

export const useSelectedProjectId = () => {
  const params = useParams();
  return params.projectId;
};

export const useSelectedUserId = () => {
  const params = useParams();
  return params.userId;
};

export const useOrganization = (organizationId: string) => {
  return useSelector((state) => {
    return state.organizations.items[organizationId];
  });
};

export const useUserRolesByOrgId = (organizationId: string) => {
  return useSelector((state) => {
    return state.organizations.userRoles.filter((roleInfo) => roleInfo.orgId === organizationId);
  });
};

export const useProject = (projectId: string) => {
  return useSelector((state) => {
    return state.projects.items[projectId];
  });
};

export const useUserRolesByProjectId = (projectId: string) => {
  return useSelector((state) => {
    return state.projects.userRoles.filter((roleInfo) => roleInfo.projectId === projectId);
  });
};

export const useProjectsByOrgId = (orgId: string) => {
  return useSelector((state) => {
    return Object.values(state.projects.items).filter(
      (project) => project?.organization_id === orgId,
    );
  });
};

export const useOrgMembers = (orgId: string) => {
  return useSelector((state) => {
    return state.organizations.userRoles
      .filter((userRole) => userRole.orgId === orgId)
      .map((userRole) => {
        const user = state.users.items[userRole.userId];
        return user ? {...user, roles: userRole.roles} : undefined;
      })
      .filter((member): member is User & {roles: string[]} => member !== undefined);
  });
};

export const useUserProjectsInOrg = (userId: string, orgId: string) => {
  return useSelector((state) => {
    return state.projects.userRoles
      .filter((userRole) => userRole.userId === userId)
      .map((userRole) => {
        const project = state.projects.items[userRole.projectId];
        if (project?.organization_id !== orgId) {
          return undefined;
        }
        return {...project, roles: userRole.roles};
      })
      .filter((project): project is Project & {roles: string[]} => project !== undefined);
  });
};

export const useProjectsByUserId = (userId: string) => {
  return useSelector((state) => {
    return Object.values(state.projects.items).filter((project) => {
      return state.projects.userRoles.find(
        (role) => role.projectId === project?.id && role.userId === userId,
      );
    });
  });
};

export const useOrganizationsByUserId = (userId: string) => {
  return useSelector((state) => {
    return Object.values(state.organizations.items).filter((org) => {
      return state.organizations.userRoles.find(
        (role) => role.orgId === org?.id && role.userId === userId,
      );
    }) as Organization[];
  });
};

export const useUserOrganizationRoles = (userId: string, orgId: string) => {
  return useSelector((state) => {
    return (
      state.organizations.userRoles.find(
        (record) => record.userId === userId && record.orgId === orgId,
      )?.roles ?? []
    );
  });
};

export const useUser = (userId: string) => {
  return useSelector((state) => {
    return state.users.items[userId];
  });
};

export const useSearchError = () => {
  return useSelector((state) => state.users.searchError);
};

export const useDatabases = (projectId: string) => {
  return useSelector((state) => state.projects.indexes[projectId]);
};

export const useCollections = (projectId: string) => {
  return useSelector((state) => state.projects.collections[projectId]);
};

export const useProjectIsLoading = () => {
  return useSelector((state) => state.projects.loading);
};

export const useIndexesAndCollectionsIsLoading = () => {
  return useSelector((state) => state.projects.servicesLoading);
};

export const useProjectQuotaLimits = (projectId: string) => {
  return useSelector((state) => {
    return state.quotaLimits.projects?.[projectId]?.quotaLimits || {};
  });
};

export const useProjectQuotaLimitsIsLoading = (projectId: string) => {
  return useSelector((state) => {
    return state.quotaLimits.projects?.[projectId]?.loading;
  });
};

export const useOrganizationQuotaUtilization = (organizationId: string) => {
  return useSelector((state) => {
    return state.quotaUtilization.organizations?.[organizationId]?.quotaUtilization || {};
  });
};

export const useOrganizationQuotaUtilizationIsLoading = (organizationId: string) => {
  return useSelector((state) => {
    return state.quotaUtilization?.organizations?.[organizationId]?.loading;
  });
};

export const useNotifications = () => {
  return useSelector((state) => state.notify.notifications);
};

export const useActiveNotification = () => {
  return useNotifications()[0];
};
